import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Catalogo from "@/pages/Catalogo";
import Plantillas from "@/pages/Plantillas";
import CatalogoReferencia from "@/pages/CatalogoReferencia";
import Sinonimos from "@/pages/Sinonimos";
import CambioPasswordObligatorio from "@/components/CambioPasswordObligatorio";
import OlvidePassword from "@/pages/OlvidePassword";
import Cotizaciones from "@/pages/Cotizaciones";
import CotizacionDetalle from "@/pages/CotizacionDetalle";
import CotizacionTrabajo from "@/pages/CotizacionTrabajo";
import Usuarios from "@/pages/Usuarios";
import Perfil from "@/pages/Perfil";
import ClienteCrear from "@/pages/ClienteCrear";
import ClientesGestion from "@/pages/ClientesGestion";
import VehiculoCrear from "@/pages/VehiculoCrear";
import VehiculosGestion from "@/pages/VehiculosGestion";
import Empleados from "@/pages/Empleados";
import Servicios from "@/pages/Servicios";
import TrabajoIngresar from "@/pages/TrabajoIngresar";
import TrabajosActivos from "@/pages/TrabajosActivos";
import TrabajoFinalizar from "@/pages/TrabajoFinalizar";
import TrabajosRealizados from "@/pages/TrabajosRealizados";
import TrabajosPendientes from "@/pages/TrabajosPendientes";
import ConfigTaller from "@/pages/ConfigTaller";
import Cronogramas from "@/pages/Cronogramas";
import Modelos from "@/pages/Modelos";
import TrabajoSolicitud from "@/pages/TrabajoSolicitud";
import TrabajoAprobacion from "@/pages/TrabajoAprobacion";
import Roles from "@/pages/Roles";
import Dashboard from "@/pages/Dashboard";
import VehiculoHistorial from "@/pages/VehiculoHistorial";
import PedidosEspeciales from "@/pages/PedidosEspeciales";
import PedidoEspecialDetalle from "@/pages/PedidoEspecialDetalle";
import ControlHonda from "@/pages/ControlHonda";
import UsoIA from "@/pages/UsoIA";
import ReporteMecanicos from "@/pages/ReporteMecanicos";

function Protected({ children, adminOnly, permiso }) {
  const { user, loading, isAdmin, tienePermiso } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Cargando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.debe_cambiar_password) return <CambioPasswordObligatorio />;
  if (adminOnly && !isAdmin) return <Navigate to="/perfil" replace />;
  if (permiso && !tienePermiso(...[].concat(permiso))) return <Navigate to="/perfil" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/olvide-password" element={<OlvidePassword />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/cotizaciones" element={<Protected permiso="cotizaciones.crear"><Cotizaciones /></Protected>} />
            <Route path="/cotizaciones/c/:id" element={<Protected permiso="cotizaciones.crear"><CotizacionTrabajo /></Protected>} />
            <Route path="/cotizaciones/:id" element={<Protected permiso="cotizaciones.crear"><CotizacionDetalle /></Protected>} />
            <Route path="/catalogo" element={<Protected permiso="mant.repuestos"><Catalogo /></Protected>} />
            <Route path="/plantillas" element={<Protected permiso="mant.plantillas"><Plantillas /></Protected>} />
            <Route path="/catalogo-referencia" element={<Protected permiso="mant.catref"><CatalogoReferencia /></Protected>} />
            <Route path="/sinonimos" element={<Protected permiso="mant.sinonimos"><Sinonimos /></Protected>} />
            <Route path="/modelos" element={<Protected permiso="mant.modelos"><Modelos /></Protected>} />
            <Route path="/clientes" element={<Protected permiso="clientes.crear"><ClienteCrear /></Protected>} />
            <Route path="/clientes/gestion" element={<Protected permiso="clientes.ver"><ClientesGestion /></Protected>} />
            <Route path="/vehiculos" element={<Protected permiso="vehiculos.crear"><VehiculoCrear /></Protected>} />
            <Route path="/vehiculos/gestion" element={<Protected permiso="vehiculos.ver"><VehiculosGestion /></Protected>} />
            <Route path="/empleados" element={<Protected permiso="mant.empleados"><Empleados /></Protected>} />
            <Route path="/servicios" element={<Protected permiso="mant.servicios"><Servicios /></Protected>} />
            <Route path="/cronogramas" element={<Protected permiso="mant.cronogramas"><Cronogramas /></Protected>} />
            <Route path="/trabajos/ingresar" element={<Protected permiso="trabajos.ingresar"><TrabajoIngresar /></Protected>} />
            <Route path="/trabajos/activos" element={<Protected permiso="trabajos.ver"><TrabajosActivos /></Protected>} />
            <Route path="/pedidos-especiales" element={<Protected permiso="pedidos.ver"><PedidosEspeciales /></Protected>} />
            <Route path="/pedidos-especiales/:id" element={<Protected permiso="pedidos.ver"><PedidoEspecialDetalle /></Protected>} />
            <Route path="/trabajos/:id/finalizar" element={<Protected permiso="trabajos.trabajar"><TrabajoFinalizar /></Protected>} />
            <Route path="/trabajos/:id/solicitud" element={<Protected permiso={["trabajos.solicitar","trabajos.aprobar"]}><TrabajoSolicitud /></Protected>} />
            <Route path="/trabajos/:id/aprobacion" element={<Protected permiso="trabajos.aprobar"><TrabajoAprobacion /></Protected>} />
            <Route path="/reportes/realizados" element={<Protected permiso="reportes.ver"><TrabajosRealizados /></Protected>} />
            <Route path="/reportes/pendientes" element={<Protected permiso="boletas.ver"><TrabajosPendientes /></Protected>} />
            <Route path="/reportes/vehiculos" element={<Protected permiso="reportes.ver"><VehiculoHistorial /></Protected>} />
            <Route path="/reportes/control-honda" element={<Protected adminOnly><ControlHonda /></Protected>} />
            <Route path="/reportes/mecanicos" element={<Protected adminOnly><ReporteMecanicos /></Protected>} />
            <Route path="/uso-ia" element={<Protected adminOnly><UsoIA /></Protected>} />
            <Route path="/config-taller" element={<Protected permiso="mant.config"><ConfigTaller /></Protected>} />
            <Route path="/roles" element={<Protected permiso="mant.roles"><Roles /></Protected>} />
            <Route path="/usuarios" element={<Protected permiso="mant.usuarios"><Usuarios /></Protected>} />
            <Route path="/perfil" element={<Protected><Perfil /></Protected>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
